import type { LucideIcon } from 'lucide-react';
import {
    LayoutDashboard,
    Users,
    ShoppingBag,
    GraduationCap,
    Calendar,
    Flag,
    Smartphone,
    Video,
    CreditCard,
    Wrench,
    BarChart3,
    Search,
    FileText,
    Fish,
    Newspaper,
    Gift,
    Receipt,
    IdCard,
    Mail,
    Megaphone,
    ListTree,
    Camera,
} from 'lucide-react';
import { storeOrdersListHref } from '@/lib/store-orders-return';
import { storeProductsListHref } from '@/lib/store-products-return';
import { STORE_NAV_SECTIONS, storeSectionForPath } from './store-nav';

export interface AdminNavChild {
    label: string;
    path: string;
    end?: boolean;
    aliases?: string[];
}

export interface AdminNavSection {
    label: string;
    items: readonly AdminNavChild[];
}

export interface AdminNavItem {
    label: string;
    path: string;
    icon: LucideIcon;
    end?: boolean;
    /** Rutas hermanas que deben marcar este ítem como activo. */
    aliases?: string[];
    /** Subgrupos. Si hay, el ítem se despliega en el sidebar. */
    sections?: readonly AdminNavSection[];
}

export interface AdminNavGroup {
    title: string;
    items: AdminNavItem[];
}

export interface AdminHubTab {
    label: string;
    path: string;
}

export interface AdminHub {
    id: string;
    tabs: AdminHubTab[];
}

export const ADMIN_NAV_GROUPS: AdminNavGroup[] = [
    {
        title: 'Operación',
        items: [
            { label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard, end: true },
            {
                label: 'Usuarios',
                path: '/users',
                icon: Users,
                aliases: ['/settings/app-rating-feedback'],
            },
            { label: 'Analytics', path: '/analytics', icon: BarChart3 },
            { label: 'Web', path: '/seo', icon: Search },
            { label: 'Reportes', path: '/moderation', icon: Flag },
        ],
    },
    {
        title: 'Comercio',
        items: [
            {
                label: 'Operaciones',
                path: '/operations/resultado',
                icon: Receipt,
                aliases: ['/operations/emitidas', '/operations/recibidas', '/operations/suplidos', '/billing'],
            },
            {
                label: 'Tienda',
                path: '/store',
                icon: ShoppingBag,
                sections: STORE_NAV_SECTIONS,
            },
            { label: 'Licencias', path: '/licenses', icon: IdCard, aliases: ['/gestiones'] },
            {
                label: 'Suscripciones',
                path: '/subscriptions',
                icon: CreditCard,
                aliases: ['/settings/subscriptions-config', '/pro-coupons'],
            },
        ],
    },
    {
        title: 'Contenido',
        items: [
            { label: 'Academia', path: '/academy', icon: GraduationCap },
            { label: 'Eventos', path: '/events', icon: Calendar, aliases: ['/jornadas'] },
            { label: 'Clubes', path: '/clubs', icon: Users, aliases: ['/brands'] },
            { label: 'Blog', path: '/blog', icon: FileText },
            { label: 'Especies', path: '/species', icon: Fish },
            { label: 'Capturas', path: '/captures', icon: Camera },
            { label: 'Catálogo captura', path: '/catch-catalog', icon: ListTree },
            { label: 'AiPeces Oficial', path: '/pescadillo-posts', icon: Megaphone },
            { label: 'Noticias', path: '/settings/fishing-news', icon: Newspaper },
        ],
    },
    {
        title: 'Comunicación',
        items: [
            {
                label: 'Comunicación',
                path: '/communications',
                icon: Mail,
                aliases: ['/notifications', '/campaigns', '/pescadillo-posts'],
            },
        ],
    },
    {
        title: 'Sistema',
        items: [
            {
                label: 'App',
                path: '/settings/app',
                icon: Smartphone,
                aliases: ['/settings/maintenance', '/settings/features', '/settings/feed-algorithm'],
            },
            {
                label: 'IA',
                path: '/settings/ai-video',
                icon: Video,
                aliases: ['/settings/chat-pescadillo'],
            },
            { label: 'Cofres', path: '/cofres', icon: Gift },
            {
                label: 'Herramientas',
                path: '/settings/tools',
                icon: Wrench,
                aliases: ['/settings/functions-status', '/settings/general', '/settings/ops-notifications'],
            },
        ],
    },
];

export const ADMIN_HUBS: AdminHub[] = [
    {
        id: 'users',
        tabs: [
            { label: 'Cuentas', path: '/users' },
            { label: 'Valoraciones', path: '/settings/app-rating-feedback' },
        ],
    },
    {
        id: 'operations',
        tabs: [
            { label: 'Resultado', path: '/operations/resultado' },
            { label: 'Emitidas', path: '/operations/emitidas' },
            { label: 'Recibidas', path: '/operations/recibidas' },
            { label: 'Suplidos', path: '/operations/suplidos' },
        ],
    },
    {
        id: 'licenses',
        tabs: [
            { label: 'Licencias', path: '/licenses' },
            { label: 'Gestiones', path: '/gestiones' },
        ],
    },
    {
        id: 'events',
        tabs: [
            { label: 'Eventos', path: '/events' },
            { label: 'Jornadas', path: '/jornadas' },
        ],
    },
    {
        id: 'clubs',
        tabs: [
            { label: 'Clubes', path: '/clubs' },
            { label: 'Marcas', path: '/brands' },
        ],
    },
    {
        id: 'communications',
        tabs: [
            { label: 'Reglas', path: '/communications' },
            { label: 'Push', path: '/notifications' },
            { label: 'Popups', path: '/campaigns' },
            { label: 'AiPeces Oficial', path: '/pescadillo-posts' },
        ],
    },
    {
        id: 'subscriptions',
        tabs: [
            { label: 'Suscriptores', path: '/subscriptions' },
            { label: 'Config Pro', path: '/settings/subscriptions-config' },
            { label: 'Cupones', path: '/pro-coupons' },
        ],
    },
    {
        id: 'app',
        tabs: [
            { label: 'Versiones', path: '/settings/app' },
            { label: 'Modo mantenimiento', path: '/settings/maintenance' },
            { label: 'Features', path: '/settings/features' },
            { label: 'Feed', path: '/settings/feed-algorithm' },
        ],
    },
    {
        id: 'ai',
        tabs: [
            { label: 'Video IA', path: '/settings/ai-video' },
            { label: 'Pescadillo', path: '/settings/chat-pescadillo' },
        ],
    },
    {
        id: 'tools',
        tabs: [
            { label: 'Diagnóstico', path: '/settings/tools' },
            { label: 'Functions', path: '/settings/functions-status' },
            { label: 'Config cruda', path: '/settings/general' },
            { label: 'Avisos ops', path: '/settings/ops-notifications' },
        ],
    },
];

/** Hub inferior en móvil. Analytics abre Tendencias. El cajón «Más» lista el resto. */
export const ADMIN_MOBILE_PRIMARY_NAV: AdminNavItem[] = [
    { label: 'Inicio', path: '/dashboard', icon: LayoutDashboard, end: true },
    {
        label: 'Usuarios',
        path: '/users',
        icon: Users,
        aliases: ['/settings/app-rating-feedback'],
    },
    { label: 'Analytics', path: '/analytics?tab=trends', icon: BarChart3 },
    { label: 'Tienda', path: '/store', icon: ShoppingBag },
];

const UUID_PATH_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function adminNavPathname(path: string): string {
    return path.split('?')[0] ?? path;
}

export function pathMatchesNavTarget(pathname: string, to: string, end?: boolean): boolean {
    const target = adminNavPathname(to);
    if (end || target === '/dashboard') return pathname === target;
    return pathname === target || pathname.startsWith(`${target}/`);
}

export function adminNavLinkIsActive(pathname: string, to: string, end?: boolean): boolean {
    return pathMatchesNavTarget(pathname, to, end);
}

export function adminNavTargetIsActive(
    pathname: string,
    target: { path: string; end?: boolean; aliases?: string[] },
): boolean {
    if (pathMatchesNavTarget(pathname, target.path, target.end)) return true;
    return (target.aliases ?? []).some((alias) => pathMatchesNavTarget(pathname, alias));
}

export function adminNavItemIsActive(pathname: string, item: AdminNavItem): boolean {
    if (adminNavTargetIsActive(pathname, item)) return true;
    return (item.sections ?? []).some((section) =>
        section.items.some((child) => adminNavTargetIsActive(pathname, child)),
    );
}

export function adminHubTabIsActive(pathname: string, tabPath: string): boolean {
    return pathMatchesNavTarget(pathname, tabPath);
}

export function adminHubForPath(pathname: string): AdminHub | null {
    const last = pathname.split('/').filter(Boolean).pop() ?? '';
    if (UUID_PATH_RE.test(last)) return null;
    return ADMIN_HUBS.find((hub) =>
        hub.tabs.some((tab) => pathMatchesNavTarget(pathname, tab.path)),
    ) ?? null;
}

export function adminMobileMoreIsActive(pathname: string, more: AdminNavItem[]): boolean {
    return more.some((item) => adminNavItemIsActive(pathname, item));
}

/** Ítems del cajón que no están ya en la barra inferior. Así «Más» no se enciende al entrar en Tienda. */
export function adminMobileOverflowItems(): AdminNavItem[] {
    const primaryPaths = new Set(
        ADMIN_MOBILE_PRIMARY_NAV.map((item) => adminNavPathname(item.path)),
    );
    return adminMobileMoreNavGroups()
        .flatMap((group) => group.items)
        .filter((item) => !primaryPaths.has(adminNavPathname(item.path)));
}

export function adminMobileMoreNavGroups(): AdminNavGroup[] {
    const primaryPaths = new Set(
        ADMIN_MOBILE_PRIMARY_NAV.map((item) => adminNavPathname(item.path)),
    );
    return ADMIN_NAV_GROUPS.map((group) => ({
        ...group,
        items: group.items.filter((item) =>
            Boolean(item.sections?.length) || !primaryPaths.has(adminNavPathname(item.path)),
        ),
    })).filter((group) => group.items.length > 0);
}

const PAGE_TITLES: Record<string, string> = {
    dashboard: 'Inicio',
    users: 'Usuarios',
    analytics: 'Analytics',
    seo: 'SEO',
    store: 'Tienda',
    licenses: 'Licencias',
    gestiones: 'Gestiones',
    brands: 'Marcas',
    academy: 'Academia',
    events: 'Eventos',
    jornadas: 'Jornadas',
    clubs: 'Clubes',
    blog: 'Blog',
    species: 'Especies',
    'catch-catalog': 'Catálogo captura',
    captures: 'Capturas',
    moderation: 'Reportes',
    notifications: 'Push',
    communications: 'Comunicación',
    campaigns: 'Campañas',
    subscriptions: 'Suscripciones',
    operations: 'Operaciones',
    resultado: 'Resultado',
    emitidas: 'Facturas emitidas',
    recibidas: 'Facturas recibidas',
    suplidos: 'Suplidos',
    billing: 'Facturación',
    cofres: 'Cofres',
    'pro-coupons': 'Cupones PRO',
    settings: 'Configuración',
    app: 'Versiones',
    maintenance: 'Modo mantenimiento',
    features: 'Features',
    'subscriptions-config': 'Config Pro',
    'app-rating-feedback': 'Valoraciones app',
    'ai-video': 'Video IA',
    'chat-pescadillo': 'Chat Pescadillo',
    'ops-notifications': 'Avisos ops',
    general: 'Config cruda',
    'feed-algorithm': 'Algoritmo Feed',
    'fishing-news': 'Noticias',
    tools: 'Herramientas',
    'functions-status': 'Functions',
    'cromos-album': 'Álbum cromos',
    fichas: 'Fichas',
    import: 'Importar fichas',
};

const DETAIL_TITLES: Record<string, string> = {
    users: 'Usuario',
    clubs: 'Club',
    brands: 'Marca',
    species: 'Especie',
};

export function adminMobilePageTitle(pathname: string): string {
    const section = storeSectionForPath(pathname);
    if (section) return section.title;
    const parts = pathname.split('/').filter(Boolean);
    if (parts[0] === 'store' && parts[1] === 'fichas') {
        if (parts[2] === 'import') return 'Importar fichas';
        if (parts[2]) return 'Producto';
        return 'Productos';
    }
    if (parts[0] === 'store' && parts[1] === 'own') return 'Productos';
    if (parts[0] === 'store' && parts[1] === 'orders' && parts[2]) return 'Pedido';
    if (parts[0] === 'store' && parts[1] === 'products' && parts[2] === 'import') return 'Importar fichas';
    if (parts[0] === 'store' && parts[1] === 'products' && parts[2] === 'stock') return 'Stock de merch';
    if (parts[0] === 'store' && parts[1] === 'products' && parts.length >= 3) return 'Producto';
    if (parts[0] === 'store' && parts[1] === 'suppliers' && parts[2]) return 'Proveedor';
    if (parts.length === 1 && parts[0] === 'store') return 'Inicio';
    if (parts.length === 0) return 'Inicio';
    const last = parts[parts.length - 1] ?? '';
    if (UUID_PATH_RE.test(last)) {
        return DETAIL_TITLES[parts[0] ?? ''] || PAGE_TITLES[parts[0] ?? ''] || 'Detalle';
    }
    return PAGE_TITLES[last] || PAGE_TITLES[parts[0] ?? ''] || last;
}

export function adminMobileParentPath(pathname: string): string | null {
    const parts = pathname.split('/').filter(Boolean);
    const last = parts[parts.length - 1] ?? '';
    if (parts[0] === 'store' && parts[1] === 'fichas' && parts[2]) {
        return storeProductsListHref() ?? '/store/products';
    }
    if (parts[0] === 'store' && parts[1] === 'products' && parts.length >= 3) {
        return storeProductsListHref() ?? '/store/products';
    }
    if (parts[0] === 'store' && parts[1] === 'suppliers' && parts[2]) {
        return '/store/suppliers';
    }
    if (parts[0] === 'store' && parts[1] === 'orders' && parts.length >= 3) {
        return storeOrdersListHref();
    }
    if (parts[0] === 'brands' && parts.length > 1) {
        return '/brands';
    }
    if (parts.length >= 2 && UUID_PATH_RE.test(last)) {
        return `/${parts.slice(0, -1).join('/')}`;
    }
    if (parts[0] === 'species' && parts[1] === 'cromos-album') {
        return '/species';
    }
    const hub = adminHubForPath(pathname);
    if (hub && !pathMatchesNavTarget(pathname, hub.tabs[0]?.path ?? '')) {
        return hub.tabs[0]?.path ?? null;
    }
    return null;
}
